// src/components/TradeModal.jsx
import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import { apiUrl } from "../config/api";
import { userAuthHeaders } from "../utils/authHeaders";

function TradeModal({ isOpen, onClose, item, tabName, mode, onTradeSubmitted }) {
  const [displayWeight, setDisplayWeight] = useState("");
  const [displayPrice, setDisplayPrice] = useState("");
  const [rawWeight, setRawWeight] = useState("");
  const [rawPrice, setRawPrice] = useState("");

  useEffect(() => {
    if (isOpen) {
      setDisplayWeight("");
      setDisplayPrice("");
      setRawWeight("");
      setRawPrice("");
    }
  }, [isOpen]);

  if (!isOpen || !item) return null;

  const isOurBuy = mode === 'buy';
  
  // محاسبه نرخ هر گرم
  const pricePerGram = isOurBuy
    ? (item.valueBuyPerGram || (item.valueBuy ? parseFloat(item.valueBuy) / 4.6082 : 0))
    : (item.valueSellPerGram || (item.valueSell ? parseFloat(item.valueSell) / 4.6082 : 0));

  // تبدیل عدد خام به رشته نمایشی (فارسی + کاما)
  const formatToDisplay = (value, { preserveDecimal = false } = {}) => {
    if (!value) return "";

    const normalizedValue = String(value);

    if (preserveDecimal) {
      const hasTrailingDot = normalizedValue.endsWith(".");
      const [integerPartRaw, decimalPart] = normalizedValue.split(".");
      const integerPart = integerPartRaw ? Number(integerPartRaw).toLocaleString('fa-IR') : "۰";

      if (hasTrailingDot) {
        return `${integerPart}.`;
      }

      if (decimalPart !== undefined) {
        return `${integerPart}.${decimalPart}`;
      }

      return integerPart;
    }

    return Number(normalizedValue).toLocaleString('fa-IR');
  };

  // تبدیل ارقام فارسی به انگلیسی
  const convertPersianToEnglish = (str) => {
    const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
    return str.replace(/[۰-۹]/g, (d) => persianDigits.indexOf(d));
  };

  // تغییر وزن -> محاسبه قیمت
  const handleWeightChange = (value) => {
    const convertedValue = convertPersianToEnglish(value);
    if (/^[0-9]*\.?[0-9]*$/.test(convertedValue)) {
      setRawWeight(convertedValue);
      setDisplayWeight(formatToDisplay(convertedValue, { preserveDecimal: true }));
      if (convertedValue && parseFloat(convertedValue) > 0) {
        const total = Math.round(parseFloat(convertedValue) * pricePerGram);
        setRawPrice(total.toString());
        setDisplayPrice(formatToDisplay(total));
      } else {
        setRawPrice("");
        setDisplayPrice("");
      }
    }
  };

  // تغییر قیمت -> محاسبه وزن
  const handlePriceChange = (value) => {
    const cleanValue = convertPersianToEnglish(value).replace(/[^0-9.]/g, '');
    if (cleanValue === "" || /^[0-9]*\.?[0-9]*$/.test(cleanValue)) {
      setRawPrice(cleanValue);
      setDisplayPrice(formatToDisplay(cleanValue));
      if (cleanValue) {
        const numValue = parseFloat(cleanValue);
        const w = numValue / pricePerGram;
        setRawWeight(w.toFixed(4));
        setDisplayWeight(formatToDisplay(w.toFixed(4), { preserveDecimal: true }));
      } else {
        setRawWeight("");
        setDisplayWeight("");
      }
    }
  };

  const handleSubmit = async () => {
    const w = parseFloat(rawWeight);
    const p = parseFloat(rawPrice);

    if (isNaN(w) || isNaN(p) || w <= 0 || p <= 0) {
      toast.error("لطفاً وزن و مبلغ معتبر وارد کنید");
      return;
    }

    let user = null;
    try {
      const userString = localStorage.getItem('user');
      if (userString) {
        user = JSON.parse(userString);
      }
    } catch (e) {
      console.error("Error parsing user from localStorage:", e);
    }

    if (!user || !user.id) {
      toast.error("لطفاً ابتدا وارد حساب کاربری شوید.");
      return;
    }

    try {
      // ساخت پیلود اولیه
      const payload = {
        userId: user.id,
        userName: user.firstName ? `${user.firstName} ${user.lastName}` : (user.username || 'کاربر مهمان'),
        itemId: item.id,
        weight: w,
        totalPrice: p,
        mode: mode
      };

      // ✅ اضافه کردن شرطی فیلد قیمت بر اساس مود
      if (mode === 'buy') {
        // برای خرید، باید نرخ خرید پایه (valueBuy) ارسال شود
        payload.valueBuy = item.valueBuy; 
      } else if (mode === 'sell') {
        // برای فروش، باید نرخ فروش پایه (valueSell) ارسال شود
        payload.valueSell = item.valueSell;
      }

      const response = await fetch(apiUrl("/api/trades"), {
        method: "POST",
        headers: userAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (result.success) {
        if (onTradeSubmitted) {
          onTradeSubmitted({
            tradeId: result.tradeId,
            userId: user.id,
            weight: w,
            totalPrice: p,
            mode: mode,
            itemName: item.title,
            pricePerGram: pricePerGram,
            baseRate: isOurBuy ? item.valueBuy : item.valueSell,
            totalPriceValue: isOurBuy ? item.valueBuy : item.valueSell,
            priceType: isOurBuy ? 'buy' : 'sell'
          });
        }
        onClose();
        toast.success(result.message);
      } else {
        toast.error(result.message || "خطا در ثبت معامله");
      }
    } catch (error) {
      console.error("Error submitting trade:", error);
      toast.error("خطا در برقراری ارتباط با سرور");
    }
  };

  const isValid = rawWeight && parseFloat(rawWeight) > 0 && rawPrice && parseFloat(rawPrice) > 0;
  const titleText = isOurBuy ? "فروش به ما" : "خرید از ما";
  const priceLabel = isOurBuy ? "نرخ خرید از ما" : "نرخ فروش به ما";
  const priceValue = isOurBuy ? item.valueBuy : item.valueSell;
  const perGramLabel = isOurBuy ? "نرخ هر گرم (خرید):" : "نرخ هر گرم (فروش):";
  const displayPricePerGram = pricePerGram;

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="trade-modal" onClick={(e) => e.stopPropagation()}>
        <div className="trade-modal-header">
          <div className="trade-modal-title">
            <span className="trade-tab-name">{tabName}</span>
            <span className="trade-separator">/</span>
            <span className="trade-item-name">{item.title}</span>
          </div>
          <button className="modal-close-btn" onClick={onClose}>✕</button>
        </div>
        
        <div className="trade-price-info">
          <div className="trade-price-item">
            <span className="trade-price-label">{priceLabel}</span>
            <span className="trade-price-value" style={{ color: isOurBuy ? "#27ae60" : "#e74c3c" }}>
              {priceValue ? Number(priceValue).toLocaleString('fa-IR') : 0} تومان
            </span>
          </div>
          <div className="trade-price-item" style={{ marginTop: "8px", opacity: 0.8, fontSize: "0.9em" }}>
            <span className="trade-price-label">{perGramLabel}</span>
            <span className="trade-price-value">
              {Math.round(displayPricePerGram).toLocaleString('fa-IR')} تومان
            </span>
          </div>
        </div>

        <div className="trade-form">
          <div className="trade-input-group">
            <label>وزن (گرم)</label>
            <input
              type="text"
              value={displayWeight}
              onChange={(e) => handleWeightChange(e.target.value)}
              placeholder="مثال: 10"
              className="trade-input"
            />
          </div>
          <div className="trade-input-group">
            <label>مبلغ کل (تومان)</label>
            <input
              type="text"
              value={displayPrice}
              onChange={(e) => handlePriceChange(e.target.value)}
              placeholder="مثال: 100,000,000"
              className="trade-input"
            />
          </div>
        </div>

        <button
          className={`trade-submit-btn ${isValid ? 'active' : 'disabled'}`}
          style={{ backgroundColor: "#ffcc00" }}
          onClick={handleSubmit}
          disabled={!isValid}
        >
          تأیید
        </button>
      </div>
    </div>,
    document.body
  );
}

export default TradeModal;
